/**
 * Makanly Design System v2.0.0 - Typography Tokens
 *
 * Apple Aesthetic / Monochrome with Warm Neutrals
 * Primary font: Inter
 *
 * @see /design/design.json for complete design system specification
 */

export const fontFamily = {
  // Primary font stack - Inter as main font
  // Falls back to system fonts for optimal performance
  sans: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"SF Pro Text"',
    'system-ui',
    '"Segoe UI"',
    'Roboto',
    'Helvetica',
    'Arial',
    'sans-serif',
  ].join(', '),

  // Display font for headings - Inter for consistency
  display: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"SF Pro Display"',
    'system-ui',
    '"Segoe UI"',
    'Roboto',
    'Helvetica',
    'Arial',
    'sans-serif',
  ].join(', '),

  // Monospace for code and OTP displays
  mono: [
    '"SF Mono"',
    '"Fira Code"',
    'Consolas',
    'Menlo',
    'Monaco',
    '"Liberation Mono"',
    '"Courier New"',
    'monospace',
  ].join(', '),
} as const;

/**
 * Primary font declaration
 * Use this when explicitly setting the brand font
 */
export const primaryFont = 'Inter' as const;

export const fontSize = {
  // Display sizes for hero text
  'display-1': ['3.5rem', { lineHeight: '1.1', letterSpacing: '-0.02em', fontWeight: '700' }],
  'display-2': ['3rem', { lineHeight: '1.1', letterSpacing: '-0.02em', fontWeight: '700' }],

  // Headlines for sections
  'headline-1': ['2.5rem', { lineHeight: '1.2', letterSpacing: '-0.01em', fontWeight: '600' }],
  'headline-2': ['2rem', { lineHeight: '1.2', letterSpacing: '-0.01em', fontWeight: '600' }],

  // Titles for cards and components
  'title-1': ['1.5rem', { lineHeight: '1.3', fontWeight: '600' }],
  'title-2': ['1.25rem', { lineHeight: '1.3', fontWeight: '600' }],
  'title-3': ['1.125rem', { lineHeight: '1.4', fontWeight: '600' }],

  // Body text
  'body-1': ['1.0625rem', { lineHeight: '1.5', fontWeight: '400' }],
  'body-2': ['0.9375rem', { lineHeight: '1.5', fontWeight: '400' }],

  // Small text and captions
  caption: ['0.8125rem', { lineHeight: '1.4', fontWeight: '400' }],
  overline: ['0.75rem', { lineHeight: '1.4', fontWeight: '500', letterSpacing: '0.05em' }],
} as const;

export const fontWeight = {
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

export type FontSize = keyof typeof fontSize;
